import { AlertCircle, Calendar, Plus, Star, UserPlus } from "lucide-react";
import { useState } from "react";
import { useMutation, useQuery } from "urql";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useAuth } from "../../contexts/AuthContext";
import {
  DeleteGoogleAccountMutation,
  InitAdditionalGoogleAccountMutation,
  InitMainGoogleAuthMutation,
  MyGoogleAccountsQuery,
  MyGoogleAuthStatusQuery,
  UpdateGoogleAccountMutation,
} from "../../graphql/googleAccount.graphql";
import { logger } from "../../lib/logger";
import { generateOAuthState } from "../../lib/oauth";

export function GoogleConnect() {
  const { user } = useAuth();
  const userId = user?.employeeInterviewInterviewerId ?? null;

  const [statusResult] = useQuery({ query: MyGoogleAuthStatusQuery });
  const [accountsResult, refetchAccounts] = useQuery({
    query: MyGoogleAccountsQuery,
  });
  const [, executeInitMain] = useMutation(InitMainGoogleAuthMutation);
  const [, executeInitAdditional] = useMutation(
    InitAdditionalGoogleAccountMutation,
  );
  const [, executeUpdate] = useMutation(UpdateGoogleAccountMutation);
  const [, executeDelete] = useMutation(DeleteGoogleAccountMutation);

  const [addingAccount, setAddingAccount] = useState(false);

  const status = statusResult.data?.myGoogleAuthStatus ?? null;
  const accounts = accountsResult.data?.myGoogleAccounts.nodes ?? [];
  const maxAccounts = accountsResult.data?.myGoogleAccounts.maxAccounts ?? 5;
  const loading = statusResult.fetching || accountsResult.fetching;

  const handleConnect = async () => {
    try {
      const state = generateOAuthState("calendar_link");
      const redirectUri = `${window.location.origin}/employee_interview/dashboard/google-callback`;
      const result = await executeInitMain({
        input: { redirectUri, state },
      });
      const authUrl = result.data?.initMainGoogleAuth.authUrl;
      if (result.error || !authUrl) {
        alert(result.error?.message || "連携の開始に失敗しました");
        return;
      }
      window.location.href = authUrl;
    } catch {
      alert("連携の開始に失敗しました");
    }
  };

  const handleAddAccount = async () => {
    if (!userId) {
      alert("ユーザー情報が取得できません。再ログインしてください。");
      return;
    }

    setAddingAccount(true);
    try {
      const state = generateOAuthState("additional_account");
      const redirectUri = `${window.location.origin}/employee_interview/dashboard/google-callback`;
      const result = await executeInitAdditional({
        input: { redirectUri, state },
      });
      const authUrl = result.data?.initAdditionalGoogleAccount.authUrl;
      if (result.error || !authUrl) {
        alert(result.error?.message || "Googleアカウントの追加に失敗しました");
        setAddingAccount(false);
        return;
      }
      window.location.href = authUrl;
    } catch (err) {
      logger.error("Failed to init OAuth", { err });
      alert("Googleアカウントの追加に失敗しました");
      setAddingAccount(false);
    }
  };

  const handleRemoveAccount = async (accountId: string, email: string) => {
    if (!window.confirm(`${email} の連携を解除しますか？`)) {
      return;
    }

    try {
      const result = await executeDelete({ id: accountId });
      if (result.error) {
        alert(result.error.message || "アカウントの削除に失敗しました");
        return;
      }
      refetchAccounts({ requestPolicy: "network-only" });
    } catch (err) {
      logger.error("Failed to remove account", { err });
      alert("アカウントの削除に失敗しました");
    }
  };

  const handleToggleActive = async (
    accountId: string,
    currentActive: boolean,
  ) => {
    try {
      const result = await executeUpdate({
        input: {
          employeeInterviewInterviewerGoogleAccountId: accountId,
          isActive: !currentActive,
        },
      });
      if (result.error) {
        alert(result.error.message || "アカウントの更新に失敗しました");
        return;
      }
      refetchAccounts({ requestPolicy: "network-only" });
    } catch (err) {
      logger.error("Failed to toggle account", { err });
      alert("アカウントの更新に失敗しました");
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-6 text-center text-[var(--color-text-secondary)]">
          読み込み中...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
      <div className="mx-auto max-w-[800px]">
        <nav className="mb-4 flex items-center gap-2 text-[13px] text-[var(--color-text-secondary)]">
          <span className="font-medium text-[var(--color-text-primary)]">
            Googleカレンダー連携
          </span>
        </nav>

        <div className="mb-8 border-[var(--color-primary-500)] border-l-4 pl-5">
          <h1 className="mb-2 font-bold text-2xl text-[var(--color-primary-500)]">
            Googleカレンダー連携
          </h1>
          <p className="text-[var(--color-text-secondary)] text-sm">
            個人のGoogleアカウントを追加連携することで、カレンダーの予定を考慮した空き枠を提示できます。
          </p>
        </div>

        {/* メインアカウント */}
        <Card className="mb-6 overflow-hidden">
          <CardHeader className="border-[var(--color-border-default)] border-b bg-[var(--color-primary-50)]">
            <CardTitle className="flex items-center gap-2 text-[var(--color-primary-500)] text-lg">
              <Star className="h-5 w-5" />
              メインアカウント（ログイン用）
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {status?.connected ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar size="lg">
                    <AvatarFallback className="bg-[var(--color-primary-500)] text-white">
                      {status.googleEmail?.[0]?.toUpperCase() || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium text-[var(--color-text-primary)]">
                      {status.googleEmail}
                    </div>
                    <div className="text-[var(--color-text-secondary)] text-xs">
                      ログイン・カレンダーイベント作成に使用
                    </div>
                  </div>
                </div>
                <Badge variant="success">連携済み</Badge>
              </div>
            ) : (
              <div className="py-4 text-center">
                <p className="mb-4 text-[var(--color-text-secondary)]">
                  メインアカウントが連携されていません
                </p>
                <Button onClick={handleConnect}>Googleカレンダーと連携</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 追加アカウント */}
        <Card className="overflow-hidden">
          <CardHeader className="flex-row items-center justify-between border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)]">
            <CardTitle className="flex items-center gap-2 text-[var(--color-primary-500)] text-lg">
              <UserPlus className="h-5 w-5" />
              追加Googleアカウント（個人カレンダー）
              <span className="ml-2 font-normal text-[var(--color-text-secondary)] text-xs">
                ({accounts.length}/{maxAccounts}個)
              </span>
            </CardTitle>
            <Button
              size="sm"
              onClick={handleAddAccount}
              disabled={
                addingAccount || accounts.length >= maxAccounts || !userId
              }
            >
              <Plus className="h-4 w-4" />
              {addingAccount ? "処理中..." : "アカウントを追加"}
            </Button>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="mb-6 flex items-start gap-3 rounded-lg bg-[var(--color-orange-50)] p-4">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-orange-700)]" />
              <div className="text-[13px] text-[var(--color-orange-700)] leading-relaxed">
                <strong>個人カレンダーを連携すると：</strong>
                <ul className="mt-2 ml-4 list-disc">
                  <li>個人カレンダーの予定も考慮して空き枠が計算されます</li>
                  <li>
                    インタビューの予定はメインアカウントのカレンダーに作成されます
                  </li>
                  <li>最大{maxAccounts}個まで追加連携できます</li>
                </ul>
              </div>
            </div>

            {!userId && (
              <div className="mb-4 rounded-lg bg-[var(--color-red-50)] p-6 text-center text-[var(--color-red-500)]">
                ユーザー情報が取得できません。再ログインしてください。
              </div>
            )}

            {userId && accounts.length === 0 ? (
              <div className="rounded-lg bg-[var(--color-bg-subtle-gray)] py-12 text-center text-[var(--color-text-tertiary)]">
                <Calendar className="mx-auto mb-4 h-12 w-12 opacity-50" />
                <div className="mb-2 text-sm">
                  追加のGoogleアカウントはありません
                </div>
                <div className="text-xs">
                  「アカウントを追加」ボタンから個人カレンダーを連携できます
                </div>
              </div>
            ) : (
              userId && (
                <div className="flex flex-col gap-3">
                  {accounts.map((account) => (
                    <div
                      key={account.employeeInterviewInterviewerGoogleAccountId}
                      className={`flex items-center justify-between rounded-lg border border-[var(--color-border-default)] p-4 ${
                        account.isActive
                          ? "bg-white"
                          : "bg-[var(--color-bg-subtle-gray)] opacity-70"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback
                            className={`text-white ${account.isActive ? "bg-[var(--color-orange-500)]" : "bg-[var(--color-grey-400)]"}`}
                          >
                            {account.googleEmail[0].toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium text-[var(--color-text-primary)]">
                            {account.googleEmail}
                          </div>
                          <div className="text-[var(--color-text-secondary)] text-xs">
                            {account.label || "個人カレンダー"} • 追加日:{" "}
                            {account.createdAt
                              ? formatDate(account.createdAt)
                              : "-"}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleToggleActive(
                              account.employeeInterviewInterviewerGoogleAccountId,
                              account.isActive,
                            )
                          }
                        >
                          {account.isActive ? "無効にする" : "有効にする"}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() =>
                            handleRemoveAccount(
                              account.employeeInterviewInterviewerGoogleAccountId,
                              account.googleEmail,
                            )
                          }
                        >
                          削除
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
